import { Component, OnDestroy } from '@angular/core';
import { omit } from 'lodash';
import { ZefReactiveComponent } from '@zerops/zef/core';
import {
  UserDataEntity,
  selectUserDataBaseActiveId,
  selectRevealedUserDataContent,
  revealUserData
} from '@zerops/zerops/core/user-data-base';
import { UserDataEditForm } from '@zerops/zui/user-data-edit-form';
import { selectZefDialogState, zefDialogClose } from '@zerops/zef/dialog';
import { AppState } from '@zerops/zerops/app';
import { formValueOnValid } from '@zerops/zef/forms';
import { select, Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { FEATURE_NAME } from './user-data-edit-dialog.constant';
import { UserDataEditDialogTranslations } from './user-data-edit-dialog.translations';
import { UserDataTypes } from '@zerops/models/user-data';

@Component({
  selector: 'z-user-data-edit-dialog',
  templateUrl: './user-data-edit-dialog.container.html',
  styleUrls: [ './user-data-edit-dialog.container.scss' ]
})
export class UserDataEditDialogContainer extends ZefReactiveComponent implements OnDestroy {

  userDataTypes = UserDataTypes;

  // # Event Streams
  onEdit$ = new Subject<void>();
  onClose$ = new Subject<void>();
  onReveal$ = new Subject<void>();

  // # Forms
  formState$ = this._userDataEditForm.state$;

  // # Data
  // -- sync
  editKey = this._userDataEntity.updateOne.type;
  revealKey = revealUserData.type;
  dialogKey = FEATURE_NAME;

  // -- async
  open$ = this._store.pipe(
    select(selectZefDialogState(FEATURE_NAME)),
    map((data) => data.state)
  );
  activeUserData$ = this._userDataEntity.entityById$(selectUserDataBaseActiveId);
  userDataEditDialogItemTranslations$ = this.translate$<UserDataEditDialogTranslations>(FEATURE_NAME);
  revealedContent$ = this._store.pipe(select(selectRevealedUserDataContent));
  formValue$ = this.formState$.pipe(map((state) => state.value));

  // A secret opens the editor with an empty content field, so changing it costs no
  // sudo at all. The decrypted value is pulled in only when the user explicitly asks
  // for it; the trigger disappears once that value is actually in the field (either
  // through this button or because the list had already revealed it).
  canReveal$ = combineLatest([
    this.formValue$,
    this.activeUserData$,
    this.revealedContent$
  ]).pipe(
    map(([ value, userData, revealed ]) => !!value.sensitive
      && !!userData
      && revealed[userData.id] === undefined
    )
  );

  // # State resolver
  state = this.$connect({
    open: this.open$,
    formState: this.formState$,
    userDataEditDialogItemTranslations: this.userDataEditDialogItemTranslations$,
    canReveal: this.canReveal$
  });

  // # Action Streams
  private _editAction$ = this.onEdit$.pipe(
    formValueOnValid(this._userDataEditForm),
    withLatestFrom(this.activeUserData$),
    map(([ d, { id } ]) => this._userDataEntity.updateOne(id, omit(d, 'type'), undefined, { type: 'pop' }))
  );

  private _closeAction$ = this.onClose$.pipe(
    map(() => zefDialogClose({ key: FEATURE_NAME }))
  );

  private _revealAction$ = this.onReveal$.pipe(
    withLatestFrom(this.activeUserData$),
    map(([ _, { id } ]) => revealUserData({ id }))
  );

  // Fill the content field once the reveal lands, which may be a whole sudo re-auth
  // round trip later - the sudo flow replays the reveal action, so waiting on the
  // revealed content itself is what survives it.
  private _fillRevealedAction$ = this.onReveal$.pipe(
    withLatestFrom(this.activeUserData$),
    switchMap(([ _, { id } ]) => this.revealedContent$.pipe(
      map((revealed) => revealed[id]),
      filter((content) => content !== undefined),
      take(1),
      map((content) => this._userDataEditForm.setValue('content', content))
    ))
  );

  constructor(
    private _store: Store<AppState>,
    private _userDataEditForm: UserDataEditForm,
    private _userDataEntity: UserDataEntity
  ) {
    super();

    this.$dispatchActions([
      this._editAction$,
      this._closeAction$,
      this._revealAction$,
      this._fillRevealedAction$
    ]);
  }

  // TODO https://github.com/typebytes/ngx-template-streams/issues/8
  ngOnDestroy() {
    super.ngOnDestroy();
  }

}
